import {
    useCallback,
    useEffect,
    useState,
    Fragment,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    View
} from "react-native";
import type PasswordResetModalProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    SelectBox,
    Dialog,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    GetAllUsersForAdminObjectType
} from "../../../../services/restAPI/actions/getAllUsersForAdmin/type";
import RESTServiceContext from "../../../../services/restAPI/context";

const PasswordResetModal = ({
    isVisible,
    onClose
}: PasswordResetModalProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        users,
        setUsers
    ] = useState<GetAllUsersForAdminObjectType[]>([]);

    const selectBoxRef = useRef<React.ElementRef<typeof SelectBox>>(null);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        selectedUserIDs,
        setSelectedUserIDs
    ] = useState<string[]>([]);

    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastFetchedSearchTextRef = useRef<string | null>(null);

    const fetchUsers = useCallback((searchText?: string) => {
        if (searchText === lastFetchedSearchTextRef.current) {
            return Promise.resolve();
        }
        lastFetchedSearchTextRef.current = searchText || "";

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        return new Promise<void>((resolve) => {
            searchTimeoutRef.current = setTimeout(async () => {
                try {
                    const requestParams: Record<string, unknown> = {
                        page: 1
                    };
                    if (searchText) {
                        requestParams.search = searchText;
                    }

                    const result = await RESTServiceContext.action("getAllUsersForAdmin", requestParams);
                    if (result.payload) {
                        setUsers(result.payload);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    resolve();
                }
            }, 500); // 500ms debounce
        });
    }, []);

    useEffect(() => {
        if (isVisible) {
            setSelectedUserIDs([]);
            lastFetchedSearchTextRef.current = null;
            fetchUsers();
        }
    }, [
        isVisible,
        fetchUsers
    ]);

    const selectBoxData = useMemo(() => {
        return users.map(u => ({
            __title: u.fullName || `${u.firstName} ${u.lastName}`,
            __key: u._id
        }));
    }, [
        users
    ]);

    const initialSelectedItems = useMemo(() => {
        return selectedUserIDs.map(id => {
            const user = users.find(u => u._id === id);
            return {
                __title: user ? user.fullName || `${user.firstName} ${user.lastName}` : id,
                __key: id
            };
        });
    }, [
        selectedUserIDs,
        users
    ]);

    const prevUsersRef = useRef<GetAllUsersForAdminObjectType[]>([]);

    useEffect(() => {
        if (users !== prevUsersRef.current && selectBoxRef.current) {
            selectBoxRef.current.updateData(selectBoxData, initialSelectedItems);
            prevUsersRef.current = users;
        }
    }, [
        initialSelectedItems,
        selectBoxData,
        users
    ]);

    const handleSendPasswordReset = () => {
        if (selectedUserIDs.length === 0) {
            NCoreUIKitSnackBar.open({
                title: "Lütfen en az bir kullanıcı seçin."
            });
            return;
        }

        setIsDialogVisible(true);
    };

    const executePasswordReset = async () => {
        try {
            setIsSubmitting(true);
            const result = await RESTServiceContext.action("sendPasswordResetLinkToUsers", {
                userIds: selectedUserIDs
            });

            if (result.isError) {
                NCoreUIKitSnackBar.open({
                    title: result.message || "Hata oluştu."
                });
            } else {
                NCoreUIKitSnackBar.open({
                    title: "Şifre sıfırlama bağlantıları başarıyla gönderildi."
                });
                setIsDialogVisible(false);
                onClose();
            }
        } catch (err) {
            console.error(err);
            NCoreUIKitSnackBar.open({
                title: "Sunucuyla iletişimde hata oluştu."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <Fragment>
        <Modal
            portalName="modal-system"
            id="password-reset-modal"
            isActive={isVisible}
            onClosed={onClose}
        >
            <View
                style={[
                    stylesheet.container,
                    {
                        padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                        gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                        backgroundColor: colors.project.brandWhite,
                        borderRadius: radiuses.lg
                    }
                ]}
            >
                <View
                    style={stylesheet.headerContainer}
                >
                    <Text
                        variant="titleLargeSize"
                    >
                        {"Şifre Sıfırlama Gönder"}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.formContainer,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <SelectBox
                        onChange={selectedItems => {
                            setSelectedUserIDs(selectedItems.map(item => item.__key as string));
                        }}
                        onSearch={(props) => {
                            props.setIsLoading(true);
                            fetchUsers(props.searchText).finally(() => {
                                props.setIsLoading(false);
                            });

                            if (!props.searchText) {
                                return props.data;
                            }

                            const searchLower = props.searchText.toLowerCase();
                            return props.data.filter(item =>
                                (item.__title || "").toLowerCase().includes(searchLower)
                            );
                        }}
                        initialSelectedItems={initialSelectedItems}
                        titleExtractor={item => item.__title}
                        key="password-reset-users-selectbox"
                        placeholder={"Kullanıcıları Seçin"}
                        keyExtractor={item => item.__key}
                        selectSheetProps={{
                            portalName: "modal-system"
                        }}
                        title={"Kullanıcıları Seçin"}
                        isShowSelectSheetTools={true}
                        isMultipleSelect={true}
                        data={selectBoxData}
                        isSearchable={true}
                        ref={selectBoxRef}
                    />
                </View>
                <View
                    style={[
                        stylesheet.footerContainer,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("members-detail-cancel")}
                        customBorderColor="secondaryButton"
                        customColor="secondaryButton"
                        onPress={onClose}
                        variant="filled"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("password-reset-send")}
                        onPress={handleSendPasswordReset}
                        isLoading={isSubmitting}
                    />
                </View>
            </View>
        </Modal>
        <Dialog
            content={`${selectedUserIDs.length} ${localize("password-reset-confirm-content")}`}
            primaryButtonProps={{
                title: localize("members-detail-approve"),
                onPress: executePasswordReset,
                isLoading: isSubmitting
            }}
            secondaryButtonProps={{
                title: localize("members-detail-cancel"),
                onPress: () => setIsDialogVisible(false)
            }}
            title={localize("areYouSure")}
            isVisible={isDialogVisible}
            variant="yes-no"
        />
    </Fragment>;
};

export default PasswordResetModal;
